/**
 * Persistent player progress — the campaign save. localStorage-backed (same
 * defensive try/catch pattern as `src/audio/Sfx.ts`), so it survives reloads and
 * degrades to in-memory if storage is unavailable (private mode, SSR).
 *
 * It records, per BUILT level, the highest difficulty tier cleared and best star
 * count, plus which world-map nodes are unlocked. The unlock graph lives in
 * `worldMapRegistry`; this module just walks it: clearing a node's level (tier
 * ≥ 1) unlocks every node whose `prereq` is that node, and flags the freshly
 * opened stop so `WorldMapScene` can play its reveal animation exactly once.
 */
import { rootNodeIds, nodesUnlockedBy, nodeForLevel } from '@/data/worldMapRegistry';

const STORAGE_KEY = 'dc_progress_v1';

interface SaveData {
  /** levelId → highest difficulty tier cleared (1+). */
  bestTier: Record<string, number>;
  /** levelId → best star count (0–3). */
  stars: Record<string, number>;
  /** Unlocked world-map node ids. */
  unlocked: string[];
  /** A node opened by the last clear, awaiting its reveal animation (or null). */
  pendingRevealNodeId: string | null;
}

function freshSave(): SaveData {
  return {
    bestTier: {},
    stars: {},
    // Chain roots are always unlocked so the journey always has a starting stop.
    unlocked: rootNodeIds(),
    pendingRevealNodeId: null,
  };
}

class ProgressStore {
  private data: SaveData;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<SaveData>;
        // Merge over a fresh save so older/partial saves gain new fields and the
        // roots stay unlocked even if an old save predates a node.
        const base = freshSave();
        return {
          bestTier: { ...base.bestTier, ...parsed.bestTier },
          stars: { ...base.stars, ...parsed.stars },
          unlocked: Array.from(new Set([...base.unlocked, ...(parsed.unlocked ?? [])])),
          pendingRevealNodeId: parsed.pendingRevealNodeId ?? null,
        };
      }
    } catch {
      /* corrupt or unavailable — fall through to a fresh save */
    }
    return freshSave();
  }

  private persist(): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      /* ignore — progress stays in-memory for the session */
    }
  }

  // ---- reads ---------------------------------------------------------------

  /** Highest difficulty tier cleared on a level (0 = never cleared). */
  getBestTier(levelId: string): number {
    return this.data.bestTier[levelId] ?? 0;
  }

  /** Best star count earned on a level (0 if never cleared). */
  getStars(levelId: string): number {
    return this.data.stars[levelId] ?? 0;
  }

  isUnlocked(nodeId: string): boolean {
    return this.data.unlocked.includes(nodeId);
  }

  /** The node awaiting a reveal animation, if any (does not consume it). */
  peekPendingReveal(): string | null {
    return this.data.pendingRevealNodeId;
  }

  // ---- writes --------------------------------------------------------------

  /**
   * Record a victory on `levelId` at `tier` with `stars`. Keeps the best of each,
   * unlocks any stops gated behind this level's node, and (if a NEW stop opened)
   * flags it for the reveal animation. Returns the newly-opened node id, if any.
   */
  recordClear(levelId: string, tier: number, stars: number): string | null {
    this.data.bestTier[levelId] = Math.max(this.getBestTier(levelId), tier);
    this.data.stars[levelId] = Math.max(this.getStars(levelId), stars);

    let newlyOpened: string | null = null;
    const node = nodeForLevel(levelId);
    if (node) {
      for (const nextId of nodesUnlockedBy(node.id)) {
        if (!this.data.unlocked.includes(nextId)) {
          this.data.unlocked.push(nextId);
          newlyOpened = nextId; // linear chain → at most one fresh stop
        }
      }
    }
    if (newlyOpened) this.data.pendingRevealNodeId = newlyOpened;
    this.persist();
    return newlyOpened;
  }

  /** Consume (clear) the pending-reveal flag once the map has played it. */
  consumePendingReveal(): string | null {
    const id = this.data.pendingRevealNodeId;
    this.data.pendingRevealNodeId = null;
    this.persist();
    return id;
  }

  /** Wipe all progress — dev/testing convenience. */
  reset(): void {
    this.data = freshSave();
    this.persist();
  }
}

export const progress = new ProgressStore();
